import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styles from "./PublicCollab.module.sass";
import { getAuthToken, isTokenExpired } from "../../utils/authHelpers";

const TEASER_COPY = "Join Postaway to access this specific opportunity plus many more hospitality collaborations.";

const getLookupParam = (type, params) => (
    type === "property" ? params.idOrSlug : params.idOrHandle
);

const setReturnToCurrentPage = () => {
    try {
        const path = `${window.location.pathname}${window.location.search || ""}`;
        sessionStorage.setItem("postaway_return_to", path);
    } catch (_) {}
};

const PublicCollab = ({ type }) => {
    const params = useParams();
    const navigate = useNavigate();
    const lookup = getLookupParam(type, params);
    const [collab, setCollab] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [checkingAccess, setCheckingAccess] = useState(true);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setError("");
            try {
                const res = await fetch(`/api/public/collab?type=${encodeURIComponent(type)}&id=${encodeURIComponent(lookup || "")}`);
                const data = await res.json();
                if (cancelled) return;

                if (res.ok && data.ok) {
                    setCollab(data);
                } else {
                    setError("This collaboration opportunity could not be found.");
                }
            } catch (_) {
                if (!cancelled) setError("We could not load this collaboration opportunity.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => { cancelled = true; };
    }, [type, lookup]);

    useEffect(() => {
        let cancelled = false;

        const redirectMembers = async () => {
            if (!collab?.id) {
                setCheckingAccess(false);
                return;
            }

            const token = getAuthToken();
            const email = (() => {
                try { return localStorage.getItem("postaway_email") || ""; } catch (_) { return ""; }
            })();

            if (!token || isTokenExpired(token) || !email) {
                setCheckingAccess(false);
                return;
            }

            try {
                const res = await fetch("/api/access/subscription", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ email }),
                });
                const data = await res.json();
                if (cancelled) return;

                const roles = data.roles || [];
                const canUseCollabBoard = res.ok && data.ok && data.active && roles.some((role) => role === "creator" || role === "host");
                if (canUseCollabBoard) {
                    const target = collab.type === "property"
                        ? `/collab-board?propertyId=${encodeURIComponent(collab.id)}`
                        : `/collab-board?profileId=${encodeURIComponent(collab.id)}`;
                    navigate(target, { replace: true });
                    return;
                }
            } catch (_) {
                // If access check fails, leave the public page available.
            }

            if (!cancelled) setCheckingAccess(false);
        };

        redirectMembers();
        return () => { cancelled = true; };
    }, [collab, navigate]);

    const handleSignupCta = () => {
        setReturnToCurrentPage();
        navigate("/profile-selector");
    };

    if (loading || checkingAccess) {
        return (
            <main className={styles.page}>
                <div className={styles.loadingCard}>Loading collaboration opportunity...</div>
            </main>
        );
    }

    if (error || !collab) {
        return (
            <main className={styles.page}>
                <div className={styles.emptyState}>
                    <h1>Opportunity not found</h1>
                    <p>{error || "This collaboration opportunity is no longer available."}</p>
                    <button type="button" onClick={() => navigate("/")} className={styles.secondaryButton}>Back to Postaway</button>
                </div>
            </main>
        );
    }

    return (
        <main className={styles.page}>
            <div className={styles.postcardBorder}>
                <div className={styles.postcardInner}>
                    <header className={styles.nav}>
                        <a href="/" className={styles.logo} aria-label="Go to Postaway home">
                            <div className={styles.textLogo}>postaway<sup className={styles.tm}>TM</sup></div>
                            <div className={styles.textTagline}>SEARCH. STAY. SHARE.</div>
                        </a>
                        <button type="button" onClick={handleSignupCta} className={styles.navCta}>Join Postaway</button>
                    </header>

                    <section className={styles.hero}>
                        <div className={styles.heroImageWrap}>
                            {collab.heroImageUrl ? (
                                <img src={collab.heroImageUrl} alt={collab.title} className={styles.heroImage} />
                            ) : (
                                <div className={styles.heroFallback}>postaway</div>
                            )}
                        </div>

                        <div className={styles.heroContent}>
                            <div className={styles.eyebrow}>Collaboration Opportunity</div>
                            <h1 className={styles.location}>{collab.location || collab.title}</h1>
                            <button
                                type="button"
                                className={styles.titleGate}
                                onClick={handleSignupCta}
                                aria-label="Sign up for full details"
                            >
                                <span className={styles.titleGateInner}>{collab.title}</span>
                                <span className={styles.titleGateOverlay}>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                        <rect x="3" y="11" width="18" height="10" rx="2" />
                                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                    </svg>
                                    Sign up for full details
                                </span>
                            </button>
                            <p className={styles.teaser}>{TEASER_COPY}</p>
                            <div className={styles.ctaRow}>
                                <button type="button" onClick={handleSignupCta} className={styles.primaryButton}>
                                    Join Postaway to See Full Details
                                </button>
                                <button type="button" onClick={handleSignupCta} className={styles.secondaryButton}>
                                    Request to Collaborate
                                </button>
                            </div>
                        </div>
                    </section>

                    <section className={styles.footerCta}>
                        <h2>Ready to explore this collab?</h2>
                        <p>Create a Postaway profile to request this opportunity and discover more creator-host collaborations.</p>
                        <button type="button" onClick={handleSignupCta} className={styles.primaryButton}>
                            Get Started
                        </button>
                    </section>
                </div>
            </div>
        </main>
    );
};

export default PublicCollab;
